import * as React from 'react'
import { DatePicker } from '@mui/x-date-pickers'
import TextField from '@mui/material/TextField'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import moment, { Moment } from 'moment'

import { errorRed, fieldBorderGray, labelGray, mainBlue, white } from '../../styles/colors'

type EventProps = {
    onChange: (value: moment.Moment) => void
    onInput?: (event: any) => void
}

type DateFieldProps = {
    required?: boolean
    dateFormat: string
    className?: string
    value: Date | null
    placeholder?: string
    label?: React.ReactNode
    invalidLabel?: string
    minDateMessage?: string
    maxDateMessage?: string
    InputProps?: any
    disablePast?: boolean
    disableFuture?: boolean
    error?: boolean
    mask?: (RegExp | string)[]
    autoOk?: boolean
    /** Kept for callers; MUI v5 DatePicker does not use this directly. */
    keyboard?: boolean
}

type Props = EventProps & DateFieldProps

const materialTheme = createTheme({
    components: {
        MuiFormControl: {
            styleOverrides: {
                root: {
                    width: '100%',
                    marginTop: '18px',
                    fontSize: '12px',
                },
            },
        },
        MuiButtonBase: {
            defaultProps: { disableRipple: true },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    width: '32px',
                    marginRight: '0',
                    '&:hover': {
                        backgroundColor: 'transparent',
                    },
                },
            },
        },
        MuiInput: {
            styleOverrides: {
                root: {
                    backgroundColor: white,
                    border: `1px solid ${fieldBorderGray}`,
                    borderRadius: '6px',
                    height: '38px',
                    '&.Mui-error': {
                        borderColor: errorRed,
                    },
                    '&:hover': {
                        borderColor: mainBlue,
                    },
                },
                formControl: {
                    display: 'flex',
                    alignItems: 'center !important',
                    paddingLeft: '10px',
                    'label + &': {
                        marginTop: '1px',
                    },
                },
                underline: {
                    '&:after': {
                        display: 'none',
                    },
                    '&:before': {
                        display: 'none',
                    },
                },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    color: labelGray,
                    marginTop: '-15px',
                    marginLeft: '5px',
                },
                formControl: {
                    transform: 'none',
                    fontSize: '12px',
                },
                shrink: {
                    transform: 'none',
                },
            },
        },
        MuiFormLabel: {
            styleOverrides: {
                root: {
                    '&.Mui-focused': {
                        color: labelGray,
                        fontSize: '12px',
                    },
                    '&.Mui-error': {
                        color: errorRed,
                    },
                },
            },
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    color: errorRed,
                    marginTop: '5px',
                    marginLeft: '5px',
                    '&.Mui-error': {
                        color: errorRed,
                    },
                },
            },
        },
        MuiInputAdornment: {
            styleOverrides: {
                positionEnd: {
                    marginLeft: 0,
                },
            },
        },
    },
    palette: {
        primary: {
            main: mainBlue,
            contrastText: white,
        },
    },
})

export default function CustomInlineDatepicker(props: Props) {
    const onChange = (value: moment.Moment) => props.onChange && props.onChange(value)
    const onInput = (event: React.FormEvent<HTMLDivElement>) => props.onInput && props.onInput(event)
    const handleDateChange = (v: Moment | null) => {
        if (v) {
            props.onChange && props.onChange(v)
        }
    }

    const {
        required,
        value,
        dateFormat,
        placeholder,
        label,
        className,
        invalidLabel: _invalidLabel,
        disablePast,
        disableFuture,
        minDateMessage: _minM,
        maxDateMessage: _maxM,
        InputProps,
        error,
        keyboard: _keyboard,
    } = props

    return (
        <ThemeProvider theme={materialTheme}>
            <DatePicker
                label={label}
                className={className}
                value={value ? moment(value) : null}
                onChange={handleDateChange}
                inputFormat={dateFormat}
                disableFuture={Boolean(disableFuture)}
                disablePast={Boolean(disablePast)}
                renderInput={params => (
                    <TextField
                        variant="standard"
                        {...params}
                        placeholder={placeholder}
                        error={error || params.error}
                        fullWidth
                        className={className}
                        required={required}
                        onInput={onInput}
                        InputProps={{
                            ...params.InputProps,
                            ...InputProps,
                        }}
                    />
                )}
            />
        </ThemeProvider>
    )
}
